import { useEffect, useRef } from "react";
import { Renderer, Stave, StaveNote, Voice, Formatter, Accidental, Beam, Dot, StaveTie, Barline } from "vexflow";
import { useKey, isMinorQuality } from "@/theory/key";
import { spellPc, toAscii } from "@/theory/spell";

export interface NotationMeasure {
  /**
   * Notes in this measure. Each entry is one chord/note. Pitches as Scientific
   * Pitch Notation: "C4", "F#5".
   *
   * `tieToNext` draws a tie from this note to the one that follows it, including
   * across a barline — which is the only way to notate a duration longer than a
   * bar. The tied note is struck once and held; it is not re-attacked.
   */
  chords: { keys: string[]; duration?: string; tieToNext?: boolean }[];
  label?: string; // e.g. "I" or "F maj"
}

export interface NotationProps {
  measures: NotationMeasure[];
  width?: number;
  height?: number;
  clef?: "treble" | "bass";
  timeSig?: string;
  highlightIndex?: number; // chord index across all measures
  /**
   * If set, the notation transposes itself by (currentTonicPc − tonicForWritten).
   * Pass the tonic the `keys` are written in so the same measures can be
   * rendered in any selected key. Example: write Twinkle in C and pass
   * `transposeFromTonicPc={0}`; if the user picks G major, the score becomes
   * the G-major version automatically.
   */
  transposeFromTonicPc?: number;
}

const PITCH_RE = /^([A-G])(#|b|##|bb)?(-?\d+)$/;

function letterToPc(letter: string, accidental: string): number {
  const base: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  let pc = base[letter];
  if (accidental === "#") pc += 1;
  else if (accidental === "##") pc += 2;
  else if (accidental === "b") pc -= 1;
  else if (accidental === "bb") pc -= 2;
  return ((pc % 12) + 12) % 12;
}

function transposePitch(p: string, semitones: number, useFlats: boolean): string {
  const m = p.match(PITCH_RE);
  if (!m) return p;
  const [, letter, acc = "", oct] = m;
  // Use the UNWRAPPED letter+accidental offset so boundary spellings keep
  // their register: B#4 is midi 72 (sounds C5), Cb4 is midi 59 (sounds B3).
  const BASE: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  let raw = BASE[letter];
  if (acc === "#") raw += 1;
  else if (acc === "##") raw += 2;
  else if (acc === "b") raw -= 1;
  else if (acc === "bb") raw -= 2;
  const octNum = parseInt(oct, 10);
  const oldMidi = (octNum + 1) * 12 + raw;
  const newMidi = oldMidi + semitones;
  const newPc = ((newMidi % 12) + 12) % 12;
  const name = toAscii(spellPc(newPc, useFlats, true));
  const newOct = Math.floor(newMidi / 12) - 1;
  return `${name}${newOct}`;
}

function pitchToVexKey(p: string): { key: string; accidental?: string } {
  const m = p.match(PITCH_RE);
  if (!m) throw new Error(`Bad pitch: ${p}`);
  const [, letter, acc, oct] = m;
  const accMap: Record<string, string> = { "#": "#", b: "b", "##": "##", bb: "bb" };
  const a = acc ? accMap[acc] : "";
  // VexFlow keys look like "c#/4" or "f/5"
  return {
    key: `${letter.toLowerCase()}${a}/${oct}`,
    accidental: a || undefined,
  };
}

export function Notation({
  measures,
  width = 720,
  height = 160,
  clef = "treble",
  timeSig = "4/4",
  highlightIndex,
  transposeFromTonicPc,
}: NotationProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const k = useKey();

  // How many semitones to shift, and which accidentals to spell with.
  // We pick the shortest direction (up or down) so registers don't drift.
  let semitones = 0;
  if (transposeFromTonicPc != null && k) {
    const raw = ((k.tonicPc - transposeFromTonicPc) % 12 + 12) % 12;
    semitones = raw > 6 ? raw - 12 : raw;
  }
  const useFlats = k?.useFlats ?? false;
  // VexFlow expects ASCII (F# not F♯, Bb not B♭) and minor keys with "m" suffix.
  // `k.tonicName` already reflects the quality (C♯ minor, not D♭ minor), and
  // `k.useFlats` is derived from tonic AND quality — so a C-minor stave now
  // draws three flats and spells its third E♭ instead of D♯.
  const keySig: string | undefined = k
    ? (() => {
        const ascii = toAscii(k.tonicName);
        return isMinorQuality(k.quality) ? `${ascii}m` : ascii;
      })()
    : undefined;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    host.innerHTML = "";

    const renderer = new Renderer(host, Renderer.Backends.SVG);
    renderer.resize(width, height);
    const ctx = renderer.getContext();
    ctx.setFont("Inter", 12);
    // Color via class — handled in index.css

    // First measure carries clef + key sig + time sig; the rest is a clean
    // continuation, so subtract that prefix only from measure 1.
    const PREFIX = 90; // px reserved on the first stave for clef/keysig/timesig
    const totalContent = width - 24;
    const restMeasures = Math.max(measures.length - 1, 0);
    const baseWidth = restMeasures > 0
      ? Math.floor((totalContent - PREFIX) / measures.length)
      : Math.floor(totalContent / Math.max(measures.length, 1));
    const firstWidth = baseWidth + PREFIX;
    let chordCounter = 0;
    let cursorX = 12;

    const allBeams: Beam[] = [];
    // Flat, measure-crossing list so a tie can reach the first note of the next bar.
    const tieChain: { note: StaveNote; tieToNext: boolean }[] = [];
    let beamGroups: ReturnType<typeof Beam.getDefaultBeamGroups> | undefined;
    try {
      beamGroups = Beam.getDefaultBeamGroups(timeSig);
    } catch {
      beamGroups = undefined; // VexFlow's own default (pairs of eighths)
    }

    measures.forEach((m, mi) => {
      const wM = mi === 0 ? firstWidth : baseWidth;
      const stave = new Stave(cursorX, 30, wM);
      cursorX += wM;
      // A musical example ends on a final double barline, the same as a piece.
      if (mi === measures.length - 1) stave.setEndBarType(Barline.type.END);
      if (mi === 0) {
        stave.addClef(clef);
        if (keySig) stave.addKeySignature(keySig);
        stave.addTimeSignature(timeSig);
      }
      stave.setContext(ctx).draw();

      const notes = m.chords.map((c) => {
        const transposed = c.keys.map((kk) =>
          semitones !== 0 ? transposePitch(kk, semitones, useFlats) : kk
        );
        const parts = transposed.map(pitchToVexKey);
        // Parse the "d" suffix(es) as augmentation dots. A dot needs two things
        // in VexFlow 4: `dots` on the note, which is what makes it LAST longer
        // (ticks — the formatter spaces the bar by these), and a Dot modifier,
        // which is what DRAWS it. Only the modifier was being added, so every
        // dotted note was drawn dotted but laid out at its undotted length —
        // a dotted minim took a minim's room and its bar read as short.
        const rawDur = c.duration ?? "q";
        // Trailing "r" marks a rest and must survive the dot-stripping:
        // "hdr" is a dotted minim rest, and the old pattern didn't match it at
        // all, so the raw string went to VexFlow and the dot was lost.
        const dotMatch = rawDur.match(/^([whq0-9]+)(d*)(r?)$/);
        const baseDur = dotMatch ? dotMatch[1] + dotMatch[3] : rawDur;
        const dotCount = dotMatch ? dotMatch[2].length : 0;
        const note = new StaveNote({
          keys: parts.map((p) => p.key),
          duration: baseDur,
          dots: dotCount,
          clef,
          // Without auto_stem VexFlow points every stem up — see ScoreView.
          auto_stem: true,
        });
        for (let d = 0; d < dotCount; d++) {
          Dot.buildAndAttach([note], { all: true });
        }
        // NOTE: don't manually add Accidental modifiers when a key signature
        // is set — Accidental.applyAccidentals will figure out the correct
        // visible accidentals (per-bar, MuseScore-style).
        if (!keySig) {
          parts.forEach((p, i) => {
            if (p.accidental) note.addModifier(new Accidental(p.accidental), i);
          });
        }
        const myIdx = chordCounter++;
        if (highlightIndex !== undefined && myIdx === highlightIndex) {
          note.setStyle({ fillStyle: "#f59e0b", strokeStyle: "#f59e0b" });
        }
        // Ties are resolved after every measure is drawn, because a tie may
        // point at a note that lives in the NEXT stave and doesn't exist yet.
        tieChain.push({ note, tieToNext: c.tieToNext === true });
        return note;
      });

      // Honour the time signature when computing the Voice's bar length.
      const [tsNum, tsBeat] = timeSig.split("/").map((n) => parseInt(n, 10));
      const voice = new Voice({
        num_beats: tsNum || 4,
        beat_value: tsBeat || 4,
      }).setStrict(false);
      voice.addTickables(notes);
      // The voice is non-strict so a short bar still draws — but a bar whose notes
      // don't add up to the time signature is a mistake in the lesson. Say so,
      // loudly enough for the live-site smoke test to catch it.
      const used = voice.getTicksUsed().value();
      const want = voice.getTotalTicks().value();
      if (used !== want) {
        console.error(`[Notation] bar ${mi + 1} holds ${(used / want) * (tsNum || 4)} of ${tsNum || 4} beats in ${timeSig}`);
      }

      // Auto-spell accidentals per the active key signature — this gives the
      // proper MuseScore look: only sharps/flats that aren't already in the
      // key signature show up, and a single accidental per pitch per bar.
      if (keySig) {
        try {
          // VexFlow 4.x accepts minor keys ("Am") directly — pass the signature
          // through unmodified so accidentals are computed against the right
          // scale map (stripping the "m" would use the MAJOR map and engrave
          // e.g. A-harmonic-minor's G# as a plain G).
          // @ts-ignore — applyAccidentals is a static method on Accidental
          Accidental.applyAccidentals([voice], keySig);
        } catch {
          /* fall through — keep notes drawn without forced accidentals */
        }
      }

      // Beam by the beat GROUPING the time signature implies. Without this
      // VexFlow falls back to pairs of eighths, which is right for 4/4 and
      // wrong the moment a compound meter (6/8, 9/8, 12/8) appears.
      try {
        const beams = Beam.generateBeams(notes, { groups: beamGroups });
        allBeams.push(...beams);
      } catch {
        /* ignore beaming errors on odd measures */
      }

      new Formatter().joinVoices([voice]).format([voice], wM - (mi === 0 ? PREFIX + 10 : 20));
      voice.draw(ctx, stave);

      if (m.label) {
        ctx.save();
        ctx.setFont("Inter", 11, "600");
        ctx.fillText(m.label, stave.getX() + (mi === 0 ? PREFIX : 4), 24);
        ctx.restore();
      }
    });

    // Draw beams after measures (Beam needs the formatted notes' positions)
    allBeams.forEach((b) => b.setContext(ctx).draw());

    // Ties last: both endpoints must already be formatted and drawn, and a tie
    // that crosses a barline joins notes on two different staves.
    tieChain.forEach((entry, i) => {
      const next = tieChain[i + 1];
      if (!entry.tieToNext || !next) return;
      try {
        new StaveTie({
          first_note: entry.note,
          last_note: next.note,
          // Tie every voice in the chord, not just the lowest.
          first_indices: entry.note.getKeys().map((_, n) => n),
          last_indices: next.note.getKeys().map((_, n) => n),
        })
          .setContext(ctx)
          .draw();
      } catch {
        /* a tie between incompatible notes shouldn't take the whole score down */
      }
    });
  }, [measures, width, height, clef, timeSig, highlightIndex, semitones, useFlats, keySig]);

  return (
    <div className="my-4 overflow-x-auto rounded-2xl border border-zinc-300 bg-[#FBFAF5] p-3 shadow-[0_8px_30px_rgba(0,0,0,0.4)]">
      <div ref={hostRef} className="text-zinc-900" />
    </div>
  );
}
